// src/pages/retail-shops/FinanceSalesReport.jsx
import React, {
  useState, useEffect, useCallback, useRef, useMemo, memo, Suspense,
} from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Card, CardContent, Chip, IconButton,
  TextField, InputAdornment, Alert, Snackbar, Fade, Avatar,
  useTheme, alpha, CircularProgress, Skeleton, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, Stack, Divider, useMediaQuery,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Button, MenuItem, Select, FormControl, InputLabel,
  LinearProgress, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Description as StatementIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

import '../../styles/Waiters.css';
import Sidebar from '../../components/finance/Sidebar';
import Header from '../../components/finance/Header';
import Footer from '../../components/finance/Footer';
import { ACCE_BRAND } from '../../theme/colors';

// ════════════════════════════════════════════
// API
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

const apiListSales = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/sales', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

/**
 * Range-scoped wallet statement endpoint.
 *   GET /api/tuckshop/wallets/{staffId}/statement
 *       ?from=YYYY-MM-DD HH:MM:SS
 *       &to=YYYY-MM-DD HH:MM:SS
 *       &page=1&page_size=200
 */
const apiWalletStatement = async (staffId, params = {}) => {
  try {
    const { data } = await api.get(`/tuckshop/wallets/${staffId}/statement`, { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// ORG
// ════════════════════════════════════════════
const ORG = {
  name: process.env.REACT_APP_ORGANISATION_NAME || 'ACCE Tuckshop',
  address: process.env.REACT_APP_RESTAURANT_ADDRESS || '',
  phone: process.env.REACT_APP_RESTAURANT_PHONE || '',
  email: process.env.REACT_APP_RESTAURANT_EMAIL || '',
  city: process.env.REACT_APP_RESTAURANT_CITY || '',
  state: process.env.REACT_APP_RESTAURANT_STATE || '',
  hours: process.env.REACT_APP_RESTAURANT_OPERATING_HOURS || '',
  supportEmail: process.env.REACT_APP_RESTAURANT_SUPPORT_EMAIL || '',
  supportPhone: process.env.REACT_APP_RESTAURANT_SUPPORT_PHONE || '',
};

const TUCKSHOP = {
  name: 'School Tuckshop',
  icon: '🏪',
  color: ACCE_BRAND.orange,
  darkColor: ACCE_BRAND.orangeDark,
  lightColor: ACCE_BRAND.orangeLight,
};

const PAYMENT_METHOD_CONFIG = {
  cash:     { label: 'Cash',     color: '#2E7D32' },
  card:     { label: 'Card',     color: '#1565C0' },
  transfer: { label: 'Transfer', color: '#6A1B9A' },
  pos:      { label: 'POS',      color: '#EF6C00' },
  mixed:    { label: 'Mixed',    color: '#B31B1B' },
  wallet:   { label: 'Wallet',   color: '#00897B' },
  unknown:  { label: 'Unknown',  color: '#9E9E9E' },
};

const ANALYTICS_LIMIT = 500;
const DETAIL_TXN_LIMIT = 200;

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `₦${n.toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
  });
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const formatDateInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const formatDateForAPI = (d) => {
  if (!d) return null;
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const getPaymentMethodColor = (m) =>
  PAYMENT_METHOD_CONFIG[String(m || '').toLowerCase()]?.color || '#9E9E9E';
const getPaymentMethodLabel = (m) =>
  PAYMENT_METHOD_CONFIG[String(m || '').toLowerCase()]?.label || m || 'Unknown';

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const round2 = (n) => Math.round(safeNumber(n) * 100) / 100;

const initials = (name) => {
  if (!name) return '?';
  return String(name).split(' ').filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join('').toUpperCase();
};

const makeTodayRange = () => {
  const s = new Date(); s.setHours(0, 0, 0, 0);
  const e = new Date(); e.setHours(23, 59, 59, 999);
  return { startDate: s, endDate: e };
};

const describeRange = (r) => {
  if (!r?.startDate || !r?.endDate) return 'All time';
  return `${formatDate(r.startDate)} → ${formatDate(r.endDate)}`;
};

// Group sales by their primary identity — wallet payer takes precedence
// so we can carry the staff_id through to the wallet statement endpoint.
const getSalePayer = (sale) => {
  if (!sale) return null;

  if (sale.payer_staff_id || sale.payer_name) {
    return {
      key: `payer:${sale.payer_staff_id || sale.payer_name}`,
      name: sale.payer_name || sale.customer_name || 'Wallet Payer',
      staff_id: sale.payer_staff_id || null,
      phone: sale.customer_phone || null,
      kind: 'payer',
    };
  }

  if (sale.customer_name) {
    return {
      key: `cust:${(sale.customer_phone || sale.customer_name).toLowerCase()}`,
      name: sale.customer_name,
      staff_id: null,
      phone: sale.customer_phone || null,
      kind: 'customer',
    };
  }

  return {
    key: 'walkin',
    name: 'Walk-in Customer',
    staff_id: null,
    phone: null,
    kind: 'walkin',
  };
};

// ════════════════════════════════════════════
// AGGREGATION
// ════════════════════════════════════════════
const aggregateByPayer = (sales) => {
  const map = new Map();

  (sales || []).forEach((s) => {
    const payer = getSalePayer(s);
    if (!map.has(payer.key)) {
      map.set(payer.key, {
        key: payer.key,
        name: payer.name,
        staff_id: payer.staff_id,
        phone: payer.phone,
        kind: payer.kind,
        sales_count: 0,
        items_count: 0,
        total_spent: 0,
        total_discount: 0,
        payment_methods: {},
        first_purchase_at: null,
        last_purchase_at: null,
        sales: [],
      });
    }
    const bucket = map.get(payer.key);

    const total = safeNumber(s.total);
    bucket.sales_count += 1;
    bucket.total_spent += total;
    bucket.total_discount += safeNumber(s.discount_total);

    const pm = String(s.payment_method || 'unknown').toLowerCase();
    if (!bucket.payment_methods[pm]) bucket.payment_methods[pm] = 0;
    bucket.payment_methods[pm] += total;

    const occurred = s.sold_at || s.created_at;
    if (occurred) {
      const t = new Date(occurred).getTime();
      if (!bucket.first_purchase_at || t < new Date(bucket.first_purchase_at).getTime()) {
        bucket.first_purchase_at = occurred;
      }
      if (!bucket.last_purchase_at || t > new Date(bucket.last_purchase_at).getTime()) {
        bucket.last_purchase_at = occurred;
      }
    }

    (s.items || []).forEach((it) => {
      bucket.items_count += safeNumber(it.quantity);
    });

    bucket.sales.push(s);
  });

  return Array.from(map.values())
    .map((b) => ({
      ...b,
      total_spent: round2(b.total_spent),
      total_discount: round2(b.total_discount),
      avg_sale: b.sales_count ? round2(b.total_spent / b.sales_count) : 0,
      sales: b.sales.sort(
        (a, b2) =>
          new Date(b2.sold_at || b2.created_at).getTime() -
          new Date(a.sold_at || a.created_at).getTime()
      ),
    }))
    .sort((a, b) => b.total_spent - a.total_spent);
};

// ════════════════════════════════════════════
// STATEMENT HTML
// ════════════════════════════════════════════
const STATEMENT_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: #fff; color: #111;
    font-family: 'Inter', 'Helvetica', 'Arial', sans-serif;
    font-size: 11px; line-height: 1.4;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .page { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 12mm 12mm 14mm 12mm; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 6mm; border-bottom: 2px solid #f97316; margin-bottom: 6mm; }
  .brand-name { font-size: 20px; font-weight: 800; color: #f97316; letter-spacing: -0.5px; margin: 0; }
  .brand-meta { font-size: 10px; color: #555; margin-top: 2px; line-height: 1.5; }
  .title { font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #111; }
  .subtitle { font-size: 11px; color: #666; margin-top: 2px; text-align: right; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; margin-bottom: 6mm; }
  .meta-card { border: 1px solid #e5e7eb; border-radius: 4px; padding: 4mm; background: #fafafa; }
  .meta-card h4 { margin: 0 0 2mm 0; font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; }
  .meta-card .val { font-size: 13px; font-weight: 800; color: #111; }
  .meta-card .sub { font-size: 10px; color: #666; margin-top: 1mm; }
  .kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 3mm; margin-bottom: 6mm; }
  .kpi { border: 1px solid #e5e7eb; border-radius: 4px; padding: 3mm; text-align: center; }
  .kpi .label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; }
  .kpi .value { font-size: 14px; font-weight: 800; color: #f97316; margin-top: 1mm; }
  .kpi .subline { font-size: 8px; color: #666; margin-top: 1mm; font-weight: 600; }
  .kpi.kpi-green .value { color: #16a34a; }
  .kpi.kpi-blue .value  { color: #2563eb; }
  .kpi.kpi-red .value   { color: #dc2626; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 4mm; page-break-inside: auto; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; page-break-after: auto; }
  th { background: #f3f4f6; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #374151; text-align: left; padding: 2mm 2mm; border-bottom: 1px solid #d1d5db; font-weight: 700; }
  td { font-size: 10px; padding: 2mm 2mm; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
  td.right { text-align: right; }
  td.mono { font-family: 'Courier New', monospace; font-size: 9.5px; }
  td.bold { font-weight: 700; }
  .row-green td { color: #16a34a; }
  .row-red td { color: #dc2626; }
  .total-row td { background: #fef3c7; font-weight: 800; font-size: 11px; border-bottom: 2px solid #f97316; }
  .section-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #111; margin: 5mm 0 2mm 0; padding-bottom: 1mm; border-bottom: 1px solid #e5e7eb; }
  .net-movement { font-size: 10px; color: #374151; font-weight: 600; margin: -3mm 0 5mm 0; }
  .footer { margin-top: 8mm; padding-top: 4mm; border-top: 1px solid #e5e7eb; font-size: 9px; color: #666; text-align: center; }
  .footer .signature { display: grid; grid-template-columns: 1fr 1fr; gap: 10mm; margin: 8mm 0 4mm 0; text-align: left; }
  .footer .signature div { border-top: 1px solid #111; padding-top: 2mm; font-size: 10px; color: #111; font-weight: 600; }
  .page-break { page-break-after: always; }
  .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
  .badge-cash { background: #dcfce7; color: #166534; }
  .badge-wallet { background: #ccfbf1; color: #115e59; }
  .badge-card { background: #dbeafe; color: #1e40af; }
  .badge-transfer { background: #ede9fe; color: #5b21b6; }
  .badge-pos { background: #ffedd5; color: #9a3412; }
  .badge-mixed { background: #fee2e2; color: #991b1b; }
  .badge-unknown { background: #f3f4f6; color: #4b5563; }
  @page { size: A4; margin: 0; }
  @media print {
    html, body { width: 210mm; }
    .page { width: 210mm; padding: 12mm; box-shadow: none; }
    .no-print { display: none !important; }
  }
`;

const buildStatementHtml = ({ payer, wallet, dateRange, cashierName }) => {
  const esc = (v) => (v == null ? '' : String(v))
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const fmt = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0.00';
    return n.toLocaleString('en-NG', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-NG', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }) : '—';

  const orgAddressLine = [ORG.address, ORG.city, ORG.state].filter(Boolean).join(', ');
  const generatedAt = new Date().toLocaleString('en-NG');

  // Payment method breakdown
  const pmEntries = Object.entries(payer.payment_methods || {})
    .map(([m, amt]) => ({
      method: m,
      amount: round2(amt),
      label: getPaymentMethodLabel(m),
    }))
    .sort((a, b) => b.amount - a.amount);

  // Sales rows
  const salesRows = payer.sales.map((s) => {
    const pm = String(s.payment_method || 'unknown').toLowerCase();
    const isWallet = pm === 'wallet';
    return `
      <tr>
        <td class="mono">${esc(s.reference)}</td>
        <td>${esc(fmtDate(s.sold_at || s.created_at))}</td>
        <td>${esc(s.cashier_name || '—')}</td>
        <td><span class="badge badge-${esc(pm)}">${esc(getPaymentMethodLabel(pm))}</span></td>
        <td class="right mono">${esc((s.items || []).length)}</td>
        <td class="right bold">₦${esc(fmt(s.total))}</td>
      </tr>
      ${
        isWallet && typeof s.wallet_balance_after === 'number'
          ? `<tr><td colspan="6" style="font-size:9px;color:#6b7280;padding-left:8mm;">
              Wallet ref: <span class="mono">${esc(s.wallet_reference || '—')}</span> ·
              Balance after: <strong>₦${esc(fmt(s.wallet_balance_after))}</strong>
             </td></tr>`
          : ''
      }
    `;
  }).join('');

  // Wallet ledger (in-range)
  const ledger = wallet?.transactions?.data || [];
  const ledgerRows = ledger.map((t) => {
    const isCredit = t.direction === 'credit';
    return `
      <tr class="${isCredit ? 'row-green' : 'row-red'}">
        <td>${esc(fmtDate(t.occurred_at || t.created_at))}</td>
        <td class="mono">${esc(t.reference)}</td>
        <td>${esc(t.transaction_type || '—')}</td>
        <td>${esc(t.description || '—')}</td>
        <td>${esc(t.source_reference || t.source || '—')}</td>
        <td class="right bold">${isCredit ? '+' : '−'}₦${esc(fmt(t.amount))}</td>
        <td class="right mono">₦${esc(fmt(t.balance_after))}</td>
      </tr>
    `;
  }).join('');

  // Payment-method totals
  const pmRowsHtml = pmEntries.map((p) => `
    <tr>
      <td>${esc(p.label)}</td>
      <td class="right">₦${esc(fmt(p.amount))}</td>
    </tr>
  `).join('');

  // ── Wallet range figures (direct from API, no client-side math) ──
  const walletRange      = wallet?.range || null;
  const openingBalance   = walletRange?.opening_balance  ?? null;
  const closingBalance   = walletRange?.closing_balance  ?? null;
  const creditsInRange   = walletRange?.credits_in_range ?? null;
  const debitsInRange    = walletRange?.debits_in_range  ?? null;
  const netMovement      = walletRange?.net_movement     ?? null;

  // Lifetime snapshot (from wallet.{balance,total_credited,total_debited})
  const lifetimeBalance  = wallet?.wallet?.balance           ?? null;
  const lifetimeCredited = wallet?.wallet?.total_credited    ?? null;
  const lifetimeDebited  = wallet?.wallet?.total_debited     ?? null;

  // Decide whether the wallet section is meaningful for this period
  const hasWalletData =
    walletRange !== null ||
    (lifetimeBalance !== null && lifetimeBalance !== undefined);

  const hasRangeActivity =
    (creditsInRange !== null && creditsInRange > 0) ||
    (debitsInRange  !== null && debitsInRange  > 0) ||
    (openingBalance !== null && openingBalance > 0) ||
    (closingBalance !== null && closingBalance > 0);

  const netLabel = (v) => {
    if (v === null || v === undefined) return '—';
    const sign = v > 0 ? '+' : v < 0 ? '−' : '';
    return `${sign}₦${fmt(Math.abs(v))}`;
  };

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8" />
<title>Statement — ${esc(payer.name)} (${esc(describeRange(dateRange))})</title>
<style>${STATEMENT_CSS}</style>
</head><body>
<div class="page">

  <!-- ── HEADER ── -->
  <div class="header">
    <div>
      <h1 class="brand-name">${esc(ORG.name)}</h1>
      <div class="brand-meta">
        ${orgAddressLine ? esc(orgAddressLine) + '<br/>' : ''}
        ${ORG.phone ? 'Tel: ' + esc(ORG.phone) + '<br/>' : ''}
        ${ORG.email ? esc(ORG.email) : ''}
      </div>
    </div>
    <div>
      <div class="title">Customer Statement</div>
      <div class="subtitle">
        Period: <strong>${esc(describeRange(dateRange))}</strong><br/>
        Generated: ${esc(generatedAt)}<br/>
        ${cashierName ? 'Prepared by: ' + esc(cashierName) : ''}
      </div>
    </div>
  </div>

  <!-- ── CUSTOMER / PAYER ── -->
  <div class="meta-grid">
    <div class="meta-card">
      <h4>Statement For</h4>
      <div class="val">${esc(payer.name)}</div>
      <div class="sub">
        ${payer.staff_id ? 'Student ID: ' + esc(payer.staff_id) + '<br/>' : ''}
        ${payer.phone ? 'Phone: ' + esc(payer.phone) + '<br/>' : ''}
        Type: ${esc(payer.kind === 'customer' ? 'Customer' : payer.kind === 'payer' ? 'Wallet Payer' : 'Walk-in')}
      </div>
    </div>
    <div class="meta-card">
      <h4>Activity</h4>
      <div class="val">${payer.sales_count} purchase${payer.sales_count === 1 ? '' : 's'}</div>
      <div class="sub">
        First: ${esc(fmtDate(payer.first_purchase_at))}<br/>
        Last: ${esc(fmtDate(payer.last_purchase_at))}
      </div>
    </div>
  </div>

  <!-- ── KPI STRIP ── -->
  <div class="kpi-row">
    <div class="kpi">
      <div class="label">Total Spent</div>
      <div class="value">₦${esc(fmt(payer.total_spent))}</div>
    </div>
    <div class="kpi kpi-blue">
      <div class="label">Avg per Sale</div>
      <div class="value">₦${esc(fmt(payer.avg_sale))}</div>
    </div>
    <div class="kpi kpi-green">
      <div class="label">Items Bought</div>
      <div class="value">${esc(payer.items_count)}</div>
    </div>
    <div class="kpi kpi-red">
      <div class="label">Discounts</div>
      <div class="value">₦${esc(fmt(payer.total_discount))}</div>
    </div>
  </div>

  ${
    hasWalletData
      ? `
  <!-- ── WALLET SUMMARY (period) ── -->
  <div class="section-title">Wallet Summary — Period</div>
  <div class="kpi-row">
    <div class="kpi">
      <div class="label">Opening Balance</div>
      <div class="value">${openingBalance !== null ? '₦' + esc(fmt(openingBalance)) : '—'}</div>
    </div>
    <div class="kpi kpi-green">
      <div class="label">Credited</div>
      <div class="value">${creditsInRange !== null ? '₦' + esc(fmt(creditsInRange)) : '—'}</div>
      <div class="subline">Lifetime: ₦${esc(fmt(lifetimeCredited ?? 0))}</div>
    </div>
    <div class="kpi kpi-red">
      <div class="label">Debited</div>
      <div class="value">${debitsInRange !== null ? '₦' + esc(fmt(debitsInRange)) : '—'}</div>
      <div class="subline">Lifetime: ₦${esc(fmt(lifetimeDebited ?? 0))}</div>
    </div>
    <div class="kpi kpi-blue">
      <div class="label">Closing Balance</div>
      <div class="value">${closingBalance !== null ? '₦' + esc(fmt(closingBalance)) : '—'}</div>
      <div class="subline">Lifetime: ₦${esc(fmt(lifetimeBalance ?? 0))}</div>
    </div>
  </div>
  <div class="net-movement">
    Net movement during period: <strong>${esc(netLabel(netMovement))}</strong>
    · Opening + Credits − Debits = ${openingBalance !== null && creditsInRange !== null && debitsInRange !== null
      ? '₦' + esc(fmt(openingBalance + creditsInRange - debitsInRange))
      : '—'}
  </div>
  ${!hasRangeActivity ? `
  <div class="net-movement" style="color:#6b7280;">
    No wallet activity in the selected period.
  </div>` : ''}
  `
      : ''
  }

  <!-- ── PURCHASES ── -->
  <div class="section-title">Purchase History (${payer.sales.length})</div>
  <table>
    <thead>
      <tr>
        <th style="width:22%">Reference</th>
        <th style="width:24%">Date</th>
        <th style="width:20%">Cashier</th>
        <th style="width:14%">Payment</th>
        <th class="right" style="width:8%">Items</th>
        <th class="right" style="width:12%">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${salesRows || '<tr><td colspan="6" style="text-align:center;color:#999;padding:6mm;">No purchases in this period.</td></tr>'}
      <tr class="total-row">
        <td colspan="5" class="right">Total Spent</td>
        <td class="right">₦${esc(fmt(payer.total_spent))}</td>
      </tr>
    </tbody>
  </table>

  <!-- ── PAYMENT BREAKDOWN ── -->
  ${
    pmEntries.length
      ? `
  <div class="section-title">Payment Method Breakdown</div>
  <table>
    <thead>
      <tr>
        <th>Method</th>
        <th class="right" style="width:30%">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${pmRowsHtml}
      <tr class="total-row">
        <td class="right">Total</td>
        <td class="right">₦${esc(fmt(payer.total_spent))}</td>
      </tr>
    </tbody>
  </table>
  `
      : ''
  }

  <!-- ── WALLET LEDGER ── -->
  ${
    ledgerRows
      ? `
  <div class="page-break"></div>
  <div class="section-title">Wallet Transaction Ledger (${ledger.length}${wallet?.transactions?.total && wallet.transactions.total > ledger.length ? ' of ' + wallet.transactions.total : ''})</div>
  <table>
    <thead>
      <tr>
        <th style="width:18%">Date</th>
        <th style="width:16%">Reference</th>
        <th style="width:12%">Type</th>
        <th style="width:22%">Description</th>
        <th style="width:14%">Source Ref</th>
        <th class="right" style="width:10%">Amount</th>
        <th class="right" style="width:10%">Balance</th>
      </tr>
    </thead>
    <tbody>
      ${ledgerRows}
    </tbody>
  </table>
  `
      : ''
  }

  <!-- ── SIGNATURE ── -->
  <div class="footer">
    <div class="signature">
      <div>Prepared By (Finance)</div>
      <div>Approved By (Manager)</div>
    </div>
    <div>
      This is a computer-generated statement. All amounts are in Nigerian Naira (₦).<br/>
      ${ORG.supportPhone ? 'For enquiries, contact: ' + esc(ORG.supportPhone) : ''}
      ${ORG.supportEmail ? ' · ' + esc(ORG.supportEmail) : ''}
    </div>
  </div>

</div>
</body></html>`;
};

// ════════════════════════════════════════════
// PRINT HELPER
// ════════════════════════════════════════════
const printHtml = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
    return;
  }

  doc.open(); doc.write(html); doc.close();

  const trigger = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    }
  };

  if (iframe.contentWindow.document.readyState === 'complete') {
    setTimeout(trigger, 200);
  } else {
    iframe.onload = () => setTimeout(trigger, 200);
  }
};

// ════════════════════════════════════════════
// KPI CARD
// ════════════════════════════════════════════
const KpiCard = memo(({ label, value, sub, icon, color }) => (
  <Card elevation={0} sx={{
    borderRadius: '14px',
    border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', height: '100%', position: 'relative', overflow: 'hidden',
    transition: 'all 0.25s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 10px 26px ${alpha(color, 0.12)}` },
    '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: color },
  }}>
    <CardContent sx={{ p: 2, pl: 2.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{
            color: '#64748B', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.6px', fontSize: '0.65rem',
            fontFamily: '"Inter", sans-serif', display: 'block', mb: 0.5,
          }}>{label}</Typography>
          <Typography variant="h6" fontWeight={800} sx={{
            fontSize: { xs: '1.05rem', sm: '1.25rem' },
            color: '#0F172A', lineHeight: 1.15,
            fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
          }}>{value}</Typography>
          {sub && (
            <Typography variant="caption" sx={{
              color: '#64748B', fontWeight: 600, fontSize: '0.68rem',
              fontFamily: '"Inter", sans-serif', display: 'block', mt: 0.4,
            }}>{sub}</Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 40, height: 40, borderRadius: '10px' }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const FinanceSalesReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeMenu, setActiveMenu] = useState('Reports');

  // Filters
  const [dateRange, setDateRange] = useState(makeTodayRange);
  const [searchValue, setSearchValue] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('ALL');
  const [payerKindFilter, setPayerKindFilter] = useState('ALL');

  // Data
  const [rawSales, setRawSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  // Preview pane
  const [selectedPayerKey, setSelectedPayerKey] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Statement generation
  const [generatingKey, setGeneratingKey] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailDialogPayer, setDetailDialogPayer] = useState(null);
  const [detailWallet, setDetailWallet] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);

  // Options
  const [includeWalletLedger, setIncludeWalletLedger] = useState(true);
  const [includePaymentBreakdown, setIncludePaymentBreakdown] = useState(true);
  const [bulkScope, setBulkScope] = useState('selected');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isMountedRef = useRef(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    if (!isMountedRef.current) return;
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  // ════════════════════════════════════════════
  // FETCH
  // ════════════════════════════════════════════
  const fetchSales = useCallback(async () => {
    if (!isMountedRef.current) return;
    setIsFetching(true);

    const params = { page: 1, page_size: ANALYTICS_LIMIT };
    if (dateRange.startDate) params.from = formatDateForAPI(dateRange.startDate);
    if (dateRange.endDate)   params.to   = formatDateForAPI(dateRange.endDate) + ' 23:59:59';
    if (paymentFilter !== 'ALL') params.payment_method = paymentFilter;
    if (searchDebounced) params.search = searchDebounced;

    const res = await apiListSales(params);
    if (!isMountedRef.current) return;

    if (res.success) {
      setRawSales(res.data || []);
    } else {
      pushSnackbar(res.error || 'Failed to load sales', 'error');
      setRawSales([]);
    }
    setIsFetching(false);
  }, [dateRange, paymentFilter, searchDebounced, pushSnackbar]);

  useEffect(() => {
    (async () => {
      await fetchSales();
      if (isMountedRef.current) setLoading(false);
    })();
  }, [fetchSales]);

  // ════════════════════════════════════════════
  // DERIVED
  // ════════════════════════════════════════════
  const allPayers = useMemo(() => aggregateByPayer(rawSales), [rawSales]);

  const filteredPayers = useMemo(() => {
    let list = allPayers;
    if (payerKindFilter !== 'ALL') list = list.filter((p) => p.kind === payerKindFilter);
    if (searchDebounced) {
      const t = searchDebounced.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(t) ||
        (p.staff_id || '').toLowerCase().includes(t) ||
        (p.phone || '').toLowerCase().includes(t)
      );
    }
    return list;
  }, [allPayers, payerKindFilter, searchDebounced]);

  const kpi = useMemo(() => {
    const totalSales = filteredPayers.reduce((a, p) => a + p.sales_count, 0);
    const totalSpent = round2(filteredPayers.reduce((a, p) => a + p.total_spent, 0));
    const totalItems = filteredPayers.reduce((a, p) => a + p.items_count, 0);
    const totalDiscount = round2(filteredPayers.reduce((a, p) => a + p.total_discount, 0));
    const avg = totalSales ? round2(totalSpent / totalSales) : 0;
    return { unique: filteredPayers.length, totalSales, totalSpent, totalItems, totalDiscount, avg };
  }, [filteredPayers]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPayers.slice(start, start + rowsPerPage);
  }, [filteredPayers, page, rowsPerPage]);

  const selectedPayer = useMemo(
    () => filteredPayers.find((p) => p.key === selectedPayerKey) || null,
    [filteredPayers, selectedPayerKey]
  );

  // ════════════════════════════════════════════
  // WALLET STATEMENT FETCH (range-scoped)
  // ════════════════════════════════════════════
  const fetchWalletStatementForPayer = useCallback(async (payer) => {
    if (!payer?.staff_id) return null;

    const params = { page: 1, page_size: DETAIL_TXN_LIMIT };
    if (dateRange.startDate) params.from = formatDateForAPI(dateRange.startDate) + ' 00:00:00';
    if (dateRange.endDate)   params.to   = formatDateForAPI(dateRange.endDate)   + ' 23:59:59';

    const res = await apiWalletStatement(payer.staff_id, params);
    return res.success ? res : null;
  }, [dateRange]);

  // ════════════════════════════════════════════
  // SINGLE PDF GENERATION
  // ════════════════════════════════════════════
  const generateStatement = useCallback(async (payer) => {
    if (!payer) return;
    setGeneratingKey(payer.key);

    let wallet = null;
    if (includeWalletLedger && payer.staff_id) {
      wallet = await fetchWalletStatementForPayer(payer);
    }

    const html = buildStatementHtml({
      payer: {
        ...payer,
        payment_methods: includePaymentBreakdown ? payer.payment_methods : {},
      },
      wallet,
      dateRange,
      cashierName: localStorage.getItem('user_full_name') || null,
    });

    printHtml(html);
    setGeneratingKey(null);
    pushSnackbar(`Statement ready for ${payer.name}`, 'success');
  }, [
    includeWalletLedger, includePaymentBreakdown, dateRange,
    fetchWalletStatementForPayer, pushSnackbar,
  ]);

  // ════════════════════════════════════════════
  // BULK PDF GENERATION
  // ════════════════════════════════════════════
  const generateBulkStatements = useCallback(async () => {
    let targets = [];
    if (bulkScope === 'selected') {
      if (!selectedPayerKey) {
        pushSnackbar('Select a payer first, or choose "Filtered" / "All".', 'warning');
        return;
      }
      const target = filteredPayers.find((p) => p.key === selectedPayerKey);
      if (target) targets = [target];
    } else if (bulkScope === 'filtered') {
      targets = filteredPayers;
    } else {
      targets = allPayers;
    }

    if (targets.length === 0) {
      pushSnackbar('No payers match the current scope.', 'warning');
      return;
    }

    if (targets.length > 15) {
      const ok = window.confirm(
        `You are about to generate ${targets.length} statements. ` +
        'Your browser will open the print dialog for each one. Continue?'
      );
      if (!ok) return;
    }

    setBulkGenerating(true);
    setBulkProgress(0);

    for (let i = 0; i < targets.length; i++) {
      const payer = targets[i];
      setBulkProgress(Math.round(((i + 1) / targets.length) * 100));

      let wallet = null;
      if (includeWalletLedger && payer.staff_id) {
        wallet = await fetchWalletStatementForPayer(payer);
      }

      const html = buildStatementHtml({
        payer: {
          ...payer,
          payment_methods: includePaymentBreakdown ? payer.payment_methods : {},
        },
        wallet,
        dateRange,
        cashierName: localStorage.getItem('user_full_name') || null,
      });

      await new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document;
        if (!doc) {
          document.body.removeChild(iframe);
          resolve();
          return;
        }
        doc.open(); doc.write(html); doc.close();

        const trigger = () => {
          try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
          finally {
            setTimeout(() => {
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              resolve();
            }, 500);
          }
        };

        if (iframe.contentWindow.document.readyState === 'complete') {
          setTimeout(trigger, 200);
        } else {
          iframe.onload = () => setTimeout(trigger, 200);
        }
      });

      await new Promise((r) => setTimeout(r, 300));
    }

    setBulkGenerating(false);
    setBulkProgress(0);
    pushSnackbar(`Generated ${targets.length} statement${targets.length === 1 ? '' : 's'}.`, 'success');
  }, [
    bulkScope, selectedPayerKey, filteredPayers, allPayers,
    includeWalletLedger, includePaymentBreakdown, dateRange,
    fetchWalletStatementForPayer, pushSnackbar,
  ]);

  // ════════════════════════════════════════════
  // DETAIL DIALOG
  // ════════════════════════════════════════════
  const openDetail = useCallback(async (payer) => {
    setDetailDialogPayer(payer);
    setDetailDialogOpen(true);
    setDetailWallet(null);
    if (payer.staff_id) {
      setDetailLoading(true);
      const w = await fetchWalletStatementForPayer(payer);
      if (isMountedRef.current) {
        setDetailWallet(w);
        setDetailLoading(false);
      }
    }
  }, [fetchWalletStatementForPayer]);

  const closeDetail = useCallback(() => {
    setDetailDialogOpen(false);
    setDetailDialogPayer(null);
    setDetailWallet(null);
  }, []);

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  if (loading) {
    return (
      <Suspense fallback={<div>Loading…</div>}>
        <div className="epos-wrapper">
          <div className="epos-main-layout">
            <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
            <div className="epos-main">
              <Header />
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={60} sx={{ color: TUCKSHOP.color }} />
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                  Loading finance reports…
                </Typography>
              </Box>
            </div>
          </div>
        </div>
      </Suspense>
    );
  }

  // Wallet range figures for the detail dialog
  const walletRange = detailWallet?.range || null;

  return (
    <Suspense fallback={<div>Loading…</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          <div className="epos-main">
            <Header />
            <div className="epos-content" style={{ background: '#f5f7fa' }}>

              {/* ══════ HERO ══════ */}
              <Box sx={{
                bgcolor: 'white', borderRadius: '16px',
                p: { xs: 1.5, sm: 2.5, md: 3 },
                mb: 3, border: `1px solid ${alpha(TUCKSHOP.color, 0.15)}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h4" fontWeight={800} sx={{
                      color: TUCKSHOP.color,
                      fontSize: { xs: '1.15rem', sm: '1.6rem', md: '1.8rem' },
                      fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
                    }}>
                      📑 Customer Sales & Wallet Report
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      mt: 0.5, fontWeight: 700, fontFamily: '"Inter", sans-serif',
                    }}>
                      {TUCKSHOP.icon} {TUCKSHOP.name} · Finance Department
                      <span style={{ marginLeft: 8, color: TUCKSHOP.color }}>
                        · {describeRange(dateRange)}
                      </span>
                    </Typography>
                  </Box>
                  <Chip
                    icon={<AssessmentIcon />}
                    label="Finance View"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(TUCKSHOP.color, 0.3), color: TUCKSHOP.color,
                      fontWeight: 700, fontFamily: '"Inter", sans-serif',
                    }}
                  />
                </Box>
              </Box>

              {/* ══════ FILTER BAR ══════ */}
              <Card elevation={0} sx={{ borderRadius: '14px', mb: 3 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search payer name, student ID, phone…"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      sx={{
                        flex: 1, minWidth: 240, maxWidth: 340,
                        '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff', fontWeight: 600 },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#94a3b8' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchValue && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchValue('')}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="From" type="date" size="small"
                      value={dateRange.startDate ? formatDateInput(dateRange.startDate) : ''}
                      onChange={(e) => setDateRange((r) => ({
                        ...r,
                        startDate: e.target.value ? new Date(e.target.value + 'T00:00:00') : null,
                      }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
                    />
                    <TextField
                      label="To" type="date" size="small"
                      value={dateRange.endDate ? formatDateInput(dateRange.endDate) : ''}
                      onChange={(e) => setDateRange((r) => ({
                        ...r,
                        endDate: e.target.value ? new Date(e.target.value + 'T23:59:59') : null,
                      }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
                    />

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel sx={{ fontWeight: 700 }}>Payment</InputLabel>
                      <Select
                        value={paymentFilter}
                        label="Payment"
                        onChange={(e) => setPaymentFilter(e.target.value)}
                        sx={{ borderRadius: '10px', bgcolor: '#fff', fontWeight: 600 }}
                      >
                        <MenuItem value="ALL" sx={{ fontWeight: 600 }}>All</MenuItem>
                        <MenuItem value="cash" sx={{ fontWeight: 600 }}>Cash</MenuItem>
                        <MenuItem value="wallet" sx={{ fontWeight: 600 }}>Wallet</MenuItem>
                        <MenuItem value="card" sx={{ fontWeight: 600 }}>Card</MenuItem>
                        <MenuItem value="transfer" sx={{ fontWeight: 600 }}>Transfer</MenuItem>
                        <MenuItem value="pos" sx={{ fontWeight: 600 }}>POS</MenuItem>
                        <MenuItem value="mixed" sx={{ fontWeight: 600 }}>Mixed</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel sx={{ fontWeight: 700 }}>Payer Type</InputLabel>
                      <Select
                        value={payerKindFilter}
                        label="Payer Type"
                        onChange={(e) => { setPayerKindFilter(e.target.value); setPage(0); }}
                        sx={{ borderRadius: '10px', bgcolor: '#fff', fontWeight: 600 }}
                      >
                        <MenuItem value="ALL" sx={{ fontWeight: 600 }}>All</MenuItem>
                        <MenuItem value="customer" sx={{ fontWeight: 600 }}>Customer</MenuItem>
                        <MenuItem value="payer" sx={{ fontWeight: 600 }}>Wallet Payer</MenuItem>
                        <MenuItem value="walkin" sx={{ fontWeight: 600 }}>Walk-in</MenuItem>
                      </Select>
                    </FormControl>

                    <Tooltip title="Refresh">
                      <IconButton
                        onClick={fetchSales}
                        disabled={isFetching}
                        sx={{
                          bgcolor: alpha(TUCKSHOP.color, 0.1),
                          '&:hover': { bgcolor: alpha(TUCKSHOP.color, 0.2) },
                          borderRadius: '10px',
                        }}
                      >
                        {isFetching ? <CircularProgress size={20} /> : <RefreshIcon sx={{ color: TUCKSHOP.color }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>

              {/* ══════ KPI CARDS ══════ */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard label="Unique Payers" value={kpi.unique} sub={`${kpi.totalSales} sales`} icon={<PeopleIcon />} color={TUCKSHOP.color} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard label="Total Spent" value={formatCurrency(kpi.totalSpent)} sub={`Avg ${formatCurrency(kpi.avg)}`} icon={"₦"} color="#2E7D32" />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard label="Items Sold" value={kpi.totalItems} sub={`${filteredPayers.length} payers`} icon={<ReceiptIcon />} color="#1565C0" />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <KpiCard label="Discounts" value={formatCurrency(kpi.totalDiscount)} sub="Applied to purchases" icon={<AssessmentIcon />} color="#D32F2F" />
                </Grid>
              </Grid>

              {/* ══════ OPTIONS + BULK ACTIONS ══════ */}
              <Card elevation={0} sx={{ borderRadius: '14px', mb: 3 }}>
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, fontFamily: '"Inter", sans-serif' }}>
                        Statement Options
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControlLabel
                          control={<Checkbox checked={includeWalletLedger} onChange={(e) => setIncludeWalletLedger(e.target.checked)} size="small" />}
                          label={<Typography variant="body2" fontWeight={600}>Include wallet ledger</Typography>}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={includePaymentBreakdown} onChange={(e) => setIncludePaymentBreakdown(e.target.checked)} size="small" />}
                          label={<Typography variant="body2" fontWeight={600}>Include payment breakdown</Typography>}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, fontFamily: '"Inter", sans-serif' }}>
                        Bulk PDF Generation
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel sx={{ fontWeight: 700 }}>Scope</InputLabel>
                          <Select
                            value={bulkScope}
                            label="Scope"
                            onChange={(e) => setBulkScope(e.target.value)}
                            sx={{ borderRadius: '10px', bgcolor: '#fff', fontWeight: 600 }}
                          >
                            <MenuItem value="selected" sx={{ fontWeight: 600 }}>Selected payer</MenuItem>
                            <MenuItem value="filtered" sx={{ fontWeight: 600 }}>All filtered ({filteredPayers.length})</MenuItem>
                            <MenuItem value="all" sx={{ fontWeight: 600 }}>Every payer ({allPayers.length})</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          onClick={generateBulkStatements}
                          disabled={bulkGenerating}
                          startIcon={bulkGenerating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                          sx={{
                            textTransform: 'none', fontWeight: 800, borderRadius: '10px',
                            bgcolor: TUCKSHOP.color, '&:hover': { bgcolor: TUCKSHOP.darkColor },
                          }}
                        >
                          {bulkGenerating ? `Generating… ${bulkProgress}%` : 'Generate Statements'}
                        </Button>
                      </Stack>
                      {bulkGenerating && (
                        <LinearProgress variant="determinate" value={bulkProgress} sx={{ mt: 1.5, borderRadius: 1 }} />
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* ══════ MAIN CONTENT: TABLE + PREVIEW ══════ */}
              <Grid container spacing={3}>
                {/* ── LEFT: Payer list ── */}
                <Grid item xs={12} lg={7}>
                  <Card elevation={0} sx={{ borderRadius: '14px', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, borderBottom: `1px solid ${alpha('#000', 0.06)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                        Payers ({filteredPayers.length})
                      </Typography>
                    </Box>

                    {isFetching && rawSales.length === 0 ? (
                      <Box sx={{ p: 3 }}>
                        {[...Array(5)].map((_, i) => (
                          <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: '8px', mb: 1 }} />
                        ))}
                      </Box>
                    ) : filteredPayers.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <StatementIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                          No payers found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Inter", sans-serif' }}>
                          Try adjusting the filters or date range.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <TableContainer>
                          <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                              <TableRow sx={{ bgcolor: alpha(TUCKSHOP.color, 0.04) }}>
                                <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Payer</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Sales</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Items</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Spent</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginated.map((p) => (
                                <TableRow
                                  key={p.key}
                                  hover
                                  selected={selectedPayerKey === p.key}
                                  onClick={() => setSelectedPayerKey(p.key)}
                                  sx={{
                                    cursor: 'pointer',
                                    '&.Mui-selected': { bgcolor: alpha(TUCKSHOP.color, 0.06) },
                                  }}
                                >
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                                      <Avatar sx={{
                                        bgcolor: alpha(p.kind === 'payer' ? '#00897B' : TUCKSHOP.color, 0.15),
                                        color: p.kind === 'payer' ? '#00897B' : TUCKSHOP.color,
                                        width: 36, height: 36, fontWeight: 800, fontSize: '0.82rem',
                                      }}>
                                        {initials(p.name)}
                                      </Avatar>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          {p.kind === 'payer' && <WalletIcon sx={{ fontSize: 13, color: '#00897B' }} />}
                                          {p.kind === 'customer' && <PersonIcon sx={{ fontSize: 13, color: '#64748B' }} />}
                                          <Typography fontWeight={700} noWrap sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                                            {p.name}
                                          </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' noWrap display="block">
                                          {p.staff_id ? `Student: ${p.staff_id}` : p.phone ? `Tel: ${p.phone}` : p.kind === 'walkin' ? 'Walk-in customer' : ''}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                                    {p.sales_count}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                                    {p.items_count}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight={800} sx={{ color: TUCKSHOP.color, fontFamily: '"Inter", sans-serif' }}>
                                      {formatCurrency(p.total_spent)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title="Preview">
                                      <IconButton size="small" onClick={() => openDetail(p)}>
                                        <InfoIcon fontSize="small" sx={{ color: '#1976D2' }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download PDF">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={generatingKey === p.key}
                                          onClick={() => generateStatement(p)}
                                        >
                                          {generatingKey === p.key
                                            ? <CircularProgress size={16} />
                                            : <PdfIcon fontSize="small" sx={{ color: '#C62828' }} />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          rowsPerPageOptions={[10, 15, 25, 50]}
                          component="div"
                          count={filteredPayers.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={(_, p) => setPage(p)}
                          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                          sx={{
                            borderTop: `1px solid ${alpha('#000', 0.06)}`,
                            '& .MuiTablePagination-select, & .MuiTablePagination-displayedRows': {
                              fontWeight: 700, fontFamily: '"Inter", sans-serif',
                            },
                          }}
                        />
                      </>
                    )}
                  </Card>
                </Grid>

                {/* ── RIGHT: Live preview ── */}
                <Grid item xs={12} lg={5}>
                  <Card elevation={0} sx={{ borderRadius: '14px', overflow: 'hidden', position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Box sx={{ p: 2, borderBottom: `1px solid ${alpha('#000', 0.06)}` }}>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                        Preview
                      </Typography>
                    </Box>

                    {!selectedPayer ? (
                      <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                        <StatementIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.3 }} />
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                          Select a payer
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Inter", sans-serif' }}>
                          Click any row on the left to preview the statement.
                        </Typography>
                      </Box>
                    ) : (
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                          <Avatar sx={{
                            width: 52, height: 52, fontWeight: 800,
                            bgcolor: alpha(TUCKSHOP.color, 0.15), color: TUCKSHOP.color,
                          }}>
                            {initials(selectedPayer.name)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={800} noWrap sx={{ fontFamily: '"Inter", sans-serif' }}>
                              {selectedPayer.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' noWrap display="block">
                              {selectedPayer.staff_id ? `Student: ${selectedPayer.staff_id}` : selectedPayer.phone || 'Walk-in customer'}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Sales</Typography>
                            <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>{selectedPayer.sales_count}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Items</Typography>
                            <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>{selectedPayer.items_count}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Total spent</Typography>
                            <Typography variant="body2" fontWeight={800} color={TUCKSHOP.color} fontFamily='"Inter", sans-serif'>
                              {formatCurrency(selectedPayer.total_spent)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Avg per sale</Typography>
                            <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>
                              {formatCurrency(selectedPayer.avg_sale)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Discounts</Typography>
                            <Typography variant="body2" fontWeight={800} color="#D32F2F" fontFamily='"Inter", sans-serif'>
                              {formatCurrency(selectedPayer.total_discount)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Period</Typography>
                            <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif' sx={{ fontSize: '0.78rem' }}>
                              {formatDate(selectedPayer.first_purchase_at)} → {formatDate(selectedPayer.last_purchase_at)}
                            </Typography>
                          </Box>
                        </Stack>

                        {Object.keys(selectedPayer.payment_methods).length > 0 && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Payment breakdown
                            </Typography>
                            <Stack spacing={0.8} sx={{ mt: 1 }}>
                              {Object.entries(selectedPayer.payment_methods)
                                .sort((a, b) => b[1] - a[1])
                                .map(([m, amt]) => (
                                  <Box key={m} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Chip
                                      size="small"
                                      label={getPaymentMethodLabel(m)}
                                      sx={{
                                        bgcolor: alpha(getPaymentMethodColor(m), 0.12),
                                        color: getPaymentMethodColor(m),
                                        fontWeight: 700, fontFamily: '"Inter", sans-serif',
                                        border: `1px solid ${alpha(getPaymentMethodColor(m), 0.2)}`,
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight={800} fontFamily='"Inter", sans-serif'>
                                      {formatCurrency(amt)}
                                    </Typography>
                                  </Box>
                                ))}
                            </Stack>
                          </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Stack direction="row" spacing={1}>
                          <Button
                            fullWidth variant="outlined"
                            startIcon={<InfoIcon />}
                            onClick={() => openDetail(selectedPayer)}
                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
                          >
                            Details
                          </Button>
                          <Button
                            fullWidth variant="contained"
                            startIcon={generatingKey === selectedPayer.key ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                            onClick={() => generateStatement(selectedPayer)}
                            disabled={generatingKey === selectedPayer.key}
                            sx={{
                              textTransform: 'none', fontWeight: 800, borderRadius: '10px',
                              bgcolor: TUCKSHOP.color, '&:hover': { bgcolor: TUCKSHOP.darkColor },
                            }}
                          >
                            Download PDF
                          </Button>
                        </Stack>
                      </CardContent>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ══════ DETAIL DIALOG ══════ */}
      <Dialog
        open={detailDialogOpen}
        onClose={closeDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          bgcolor: '#f8f9fa',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar sx={{ bgcolor: alpha(TUCKSHOP.color, 0.15), color: TUCKSHOP.color, fontWeight: 800 }}>
              {initials(detailDialogPayer?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap sx={{ fontFamily: '"Inter", sans-serif' }}>
                {detailDialogPayer?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' noWrap display="block">
                {detailDialogPayer?.staff_id ? `Student: ${detailDialogPayer.staff_id}` : detailDialogPayer?.phone || 'Walk-in customer'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeDetail}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailDialogPayer && (
            <>
              {/* ── KPI strip ── */}
              <Box sx={{ p: 3, bgcolor: alpha(TUCKSHOP.color, 0.03), borderBottom: `1px solid ${alpha('#000', 0.06)}` }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Sales</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                      {detailDialogPayer.sales_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Items</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                      {detailDialogPayer.items_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Spent</Typography>
                    <Typography variant="h6" fontWeight={800} color={TUCKSHOP.color} sx={{ fontFamily: '"Inter", sans-serif' }}>
                      {formatCurrency(detailDialogPayer.total_spent)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Avg Sale</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                      {formatCurrency(detailDialogPayer.avg_sale)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* ── Wallet summary (period-scoped) ── */}
              {(detailLoading || detailWallet) && (
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha('#000', 0.06)}` }}>
                  {detailLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary" fontFamily='"Inter", sans-serif'>
                        Loading wallet statement…
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
                        Wallet Summary — Period ({describeRange(dateRange)})
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Opening</Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                            {formatCurrency(walletRange?.opening_balance)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Credited</Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ color: '#2E7D32', fontFamily: '"Inter", sans-serif' }}>
                            {formatCurrency(walletRange?.credits_in_range)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' display="block">
                            Lifetime: {formatCurrency(detailWallet?.wallet?.total_credited)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Debited</Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ color: '#D32F2F', fontFamily: '"Inter", sans-serif' }}>
                            {formatCurrency(walletRange?.debits_in_range)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' display="block">
                            Lifetime: {formatCurrency(detailWallet?.wallet?.total_debited)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>Closing</Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ color: '#00897B', fontFamily: '"Inter", sans-serif' }}>
                            {formatCurrency(walletRange?.closing_balance)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily='"Inter", sans-serif' display="block">
                            Lifetime: {formatCurrency(detailWallet?.wallet?.balance)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 1.5 }}>
                        <Chip
                          size="small"
                          label={`Net movement: ${
                            walletRange?.net_movement === null || walletRange?.net_movement === undefined
                              ? '—'
                              : (walletRange.net_movement >= 0 ? '+' : '−') + formatCurrency(Math.abs(walletRange.net_movement))
                          }`}
                          sx={{
                            fontWeight: 700, fontFamily: '"Inter", sans-serif',
                            bgcolor: alpha('#0F172A', 0.06), color: '#0F172A',
                          }}
                        />
                        <Chip
                          size="small"
                          label={detailWallet?.wallet?.is_locked ? 'Wallet Locked' : 'Wallet Active'}
                          sx={{
                            ml: 1,
                            fontWeight: 700, fontFamily: '"Inter", sans-serif',
                            bgcolor: alpha(detailWallet?.wallet?.is_locked ? '#D32F2F' : '#2E7D32', 0.12),
                            color: detailWallet?.wallet?.is_locked ? '#D32F2F' : '#2E7D32',
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* ── Purchases ── */}
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha('#000', 0.06)}` }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
                  Purchases ({detailDialogPayer.sales.length})
                </Typography>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Ref</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Payment</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailDialogPayer.sales.map((s) => {
                        const pm = String(s.payment_method || 'unknown').toLowerCase();
                        return (
                          <TableRow key={s.id} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700 }}>
                              {s.reference}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {formatDateTime(s.sold_at || s.created_at)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={getPaymentMethodLabel(pm)}
                                sx={{
                                  bgcolor: alpha(getPaymentMethodColor(pm), 0.12),
                                  color: getPaymentMethodColor(pm),
                                  fontWeight: 700, fontFamily: '"Inter", sans-serif',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={800} color={TUCKSHOP.color} sx={{ fontFamily: '"Inter", sans-serif' }}>
                                {formatCurrency(s.total)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* ── Wallet ledger (period-scoped) ── */}
              {detailWallet?.transactions?.data?.length > 0 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
                    Wallet Ledger ({detailWallet.transactions.data.length} of {detailWallet.transactions.total})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Ref</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Type</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Amount</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Balance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailWallet.transactions.data.map((t) => {
                          const isCredit = t.direction === 'credit';
                          return (
                            <TableRow key={t.id} hover>
                              <TableCell sx={{ fontSize: '0.75rem' }}>
                                {formatDateTime(t.occurred_at || t.created_at)}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 700 }}>
                                {t.reference}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={t.transaction_type}
                                  sx={{
                                    bgcolor: alpha(isCredit ? '#2E7D32' : '#D32F2F', 0.12),
                                    color: isCredit ? '#2E7D32' : '#D32F2F',
                                    fontWeight: 700, fontFamily: '"Inter", sans-serif',
                                    textTransform: 'capitalize',
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{
                                fontWeight: 800,
                                color: isCredit ? '#2E7D32' : '#D32F2F',
                                fontFamily: '"Inter", sans-serif',
                              }}>
                                {isCredit ? '+' : '−'}{formatCurrency(t.amount)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
                                {formatCurrency(t.balance_after)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha('#000', 0.06)}`, gap: 1 }}>
          <Button onClick={closeDetail} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={() => detailDialogPayer && generateStatement(detailDialogPayer)}
            sx={{
              textTransform: 'none', fontWeight: 800, borderRadius: '10px',
              bgcolor: TUCKSHOP.color, '&:hover': { bgcolor: TUCKSHOP.darkColor },
            }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════ SNACKBAR ══════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled"
          sx={{ borderRadius: '10px', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Suspense>
  );
};

export default FinanceSalesReport;